import { EventEmitter, Injectable, OnDestroy, inject } from "@angular/core";

import { LocalStorageProvider } from "../storage-provider/local-storage-provider";
import { HttpParams } from "@angular/common/http";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";

@Injectable()
export class GridParams implements OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  public static DEFAULTS = "gridParamsDefaults";
  public static STORAGE_KEY = "gridParamsStorageKey";
  public static URL_KEYS = "gridParamsUrlKeys";

  public page: number;
  public limit: number;
  public first: number;
  public defaults: { [key: string]: any } = {};
  public params: { [key: string]: any } = {};
  public urlParams: { [key: string]: any } = {};
  public onChange: EventEmitter<any> = new EventEmitter<any>();
  private readonly urlKeys: string[] = ["page", "limit", "search"];
  private readonly storage: LocalStorageProvider;
  private readonly storageKey: string;
  private baseDefaults: { [key: string]: any } = {
    page: 1,
    limit: 25,
    search: null,
  };
  private subscription = new Subscription();

  constructor() {
    const defaults = inject<any>(GridParams.DEFAULTS as any, {
      optional: true,
    });
    const storageKey = inject<string>(GridParams.STORAGE_KEY as any, {
      optional: true,
    });
    const urlKeys = inject<string[]>(GridParams.URL_KEYS as any, {
      optional: true,
    });

    if (defaults) {
      this.setDefaults(defaults);
    }
    if (storageKey) {
      this.storage = new LocalStorageProvider();
      this.storageKey = storageKey;
      this.loadParamsFromLocalStorage();
    }
    if (urlKeys) {
      this.urlKeys = urlKeys;
    }
    this.loadParamsFromActivatedRoute();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public setParams(
    params: { [key: string]: any },
    updateParams: boolean = true,
  ) {
    Object.keys(params).forEach((paramKey) => {
      this.setParam(paramKey, params[paramKey], false);
    });

    if (updateParams) {
      this.updateParams();
    }
  }

  public setParam(name: string, value: any, updateParams: boolean = true) {
    if (this.urlKeys.indexOf(name) === -1) {
      this.params[name] = value;
      if (
        Object.prototype.hasOwnProperty.call(this.defaults, name) &&
        this.defaults[name] === value
      ) {
        delete this.params[name];
      }
      if (Object.prototype.hasOwnProperty.call(this.urlParams, name)) {
        delete this.urlParams[name];
      }
    } else {
      this.urlParams = { ...this.urlParams, [name]: value };
      if (
        !value &&
        Object.prototype.hasOwnProperty.call(this.urlParams, name)
      ) {
        delete this.urlParams[name];
      }
    }

    if (updateParams) {
      this.updateParams();
    }
  }

  public isDefaultParam(name: string): boolean {
    if (!Object.prototype.hasOwnProperty.call(this.defaults, name)) {
      return false;
    }

    return !Object.prototype.hasOwnProperty.call(this.params, name);
  }

  public unsetParam(name: string, unsetDefaults: boolean = false) {
    if (Object.prototype.hasOwnProperty.call(this.params, name)) {
      delete this.params[name];
    }
    if (Object.prototype.hasOwnProperty.call(this.urlParams, name)) {
      delete this.urlParams[name];
    }
    if (
      unsetDefaults &&
      Object.prototype.hasOwnProperty.call(this.defaults, name)
    ) {
      delete this.defaults[name];
    }

    this.updatePublicParams();
  }

  public getParam(name: string): any {
    if (Object.prototype.hasOwnProperty.call(this.urlParams, name)) {
      return this.urlParams[name] || "";
    }

    if (Object.prototype.hasOwnProperty.call(this.params, name)) {
      return this.params[name];
    }

    if (Object.prototype.hasOwnProperty.call(this.defaults, name)) {
      return this.defaults[name];
    }

    return null;
  }

  public getHttpParams(): HttpParams {
    let params: HttpParams = new HttpParams();

    for (const source of [this.defaults, this.params, this.urlParams]) {
      for (const key in source) {
        if (
          Object.prototype.hasOwnProperty.call(source, key) &&
          !this.isEmptyParamValue(source[key])
        ) {
          params = params.set(key, source[key]);
        }
      }
    }

    return params;
  }

  // An empty array stringifies to "", and a backend criterion of "" filters the
  // list down to nothing instead of being ignored. Drop the param instead.
  private isEmptyParamValue(value: any): boolean {
    return value === null || (Array.isArray(value) && value.length === 0);
  }

  private updateParams() {
    this.updateUrlParams();
    this.updatePublicParams();
    this.storeParamsInLocalStorage();
    this.onChange.emit(null);
  }

  private loadParamsFromActivatedRoute() {
    this.subscription.add(
      this.activatedRoute.queryParams.subscribe((changedParams) => {
        this.urlParams = { ...changedParams };
      }),
    );
  }

  private loadParamsFromLocalStorage() {
    if (this.storage) {
      const params = this.storage.get(this.storageKey);
      if (params) {
        Object.keys(params).forEach((paramKey) => {
          this.setParam(paramKey, params[paramKey], false);
        });
      }
    }
  }

  private storeParamsInLocalStorage() {
    if (this.storage) {
      const paramsToSave = Object.assign({}, this.params);
      delete paramsToSave["page"];
      this.storage.set(this.storageKey, paramsToSave);
    }
  }

  private setDefaults(defaults: { [key: string]: any }) {
    this.defaults = this.baseDefaults;

    for (const key in defaults) {
      if (!Object.prototype.hasOwnProperty.call(defaults, key)) {
        continue;
      }

      if (
        Object.prototype.hasOwnProperty.call(this.params, key) &&
        this.params[key] === defaults[key]
      ) {
        delete this.params[key];
      }

      this.defaults[key] = defaults[key];
    }

    this.updatePublicParams();
  }

  private updatePublicParams() {
    const pageParam: any = this.getParam("page");
    const limitParam: any = this.getParam("limit");

    if (null === pageParam) {
      this.page = 1;
    } else {
      this.page = +pageParam;
    }

    if (null === limitParam) {
      this.limit = 1;
    } else {
      this.limit = +limitParam;
    }

    this.first = (this.page - 1) * this.limit;
  }

  private updateUrlParams() {
    this.router.navigate([], {
      queryParams: this.urlParams,
      relativeTo: this.activatedRoute,
    });
  }
}
